import {reactive} from "vue";
import useConnector from "./ConnectorComposition";

const useCategoryTag = () => {

  //通信のための Composition を用意
  const _connector = useConnector();

  //この Composition が扱う主要データ
  const state = reactive({
    //カテゴリ
    categories: [],

    //キャンペーン
    campaigns: [],

    //カテゴリキャンペーン
    categoryCampaigns: [],

    //何のカテゴリーにも紐づかない際に属させるカテゴリー id
    otherCategoryId: 0,

    //仮抑えキャンペーンタグ ID
    temporaryCampaignTagIds: [],

    //エラーメッセージ
    errors: []
  });

  /**
   * 初期 state をグローバル変数より import
   */
  const init = () => {
    state.categories = categories;
    state.campaigns = campaigns;
    state.categoryCampaigns = categoryCampaigns;
    state.otherCategoryId = otherCategoryId;
  };

  /**
   * state.temporaryCampaignTagIds の内容を state.campaigns と同期させる
   * @param categoryIndex
   */
  const syncTemporaryCampaignTagIds = (categoryIndex) => {
    //参照カテゴリが存在しなければ何もしない
    if ( ! state.categories[categoryIndex]) {
      return;
    }

    //一仮キャンペーンを空にする
    state.temporaryCampaignTagIds = [];

    //参照カテゴリのタグが既にキャンペーンタグか判定
    for (const campaignTag of state.categories[categoryIndex].tags.filter(tag => state.campaigns.some(campaign => Number(campaign.tag_id) === Number(tag.id)))) {
      state.temporaryCampaignTagIds.push(Number(campaignTag.id));
    }
  };

  /**
   * 仮押さえキャンペーンの toggleTemporaryCampaign において指定タグをトグルさせる
   * @param tagId
   */
  const changeTemporaryCampaign = (tagId, check) => {
    //既にキャンペーン指定が存在しているか判定
    const campaignIndex = state.temporaryCampaignTagIds.findIndex(campaign => Number(campaign) === Number(tagId));

    //存在しない場合は追加
    if (check && campaignIndex === -1) {
      state.temporaryCampaignTagIds.push(Number(tagId));
    }

    //存在すれば削除
    else if ( ! check && campaignIndex !== -1) {
      state.temporaryCampaignTagIds.splice(campaignIndex, 1);
    }
  };

  /**
   * 空のカテゴリを追加する
   */
  const appendEmptyCategory = () => {
    state.categories.push({
      created_at: null,
      deleted_at: null,
      id: state.categories.reduce((before, current) => before > Number(current.id) ? Number(current.id) : before, 99999999999) - 2,
      name: '',
      order_number: state.categories.reduce((before, current) => before < Number(current.order_number) ? Number(current.order_number) : current, 0) + 1,
      tags: [],
      thumbnail: 'noNameUser.png',
      updated_at: null
    });
  };

  /**
   * タグを新規に追加する
   * @param name
   * @param categoryIndex
   */
  const appendTag = (name, categoryIndex) => {
    //名前が空
    if (name === '') {
      //エラーを登録
      state.errors = ['タグ名が入力されていません'];

      //失敗を返す
      return false;
    }

    //カテゴリが見つからない
    if (state.categories[categoryIndex] === undefined) {
      return;
    }

    //カテゴリ内で最小の ID, 最大の order_number を算出
    const minId = state.categories[categoryIndex].tags.reduce((before, current) => before > Number(current.id) ? Number(current.id) : before, -1) - 2;
    const maxOrderNumber = state.categories[categoryIndex].tags.reduce((before, current) => before < Number(current.order_number) ? Number(current.order_number) : Number(current.order_number), 0) + 1;

    //追加
    state.categories[categoryIndex].tags.push({
      created_at: null,
      deleted_at: null,
      id: minId,
      name: name,
      order_number: maxOrderNumber,
      post_category_id: state.categories[categoryIndex].id,
      updated_at: null
    });
  };

  /**
   * カテゴリ名、カテゴリアイコン、新規追加タグを更新、挿入する
   * @param categoryIndex
   * @param otherCategoryId
   * @returns {Promise<boolean>}
   */
  const setCategory = async (categoryIndex, otherCategoryId) => {
    //エラーを初期化
    state.errors = [];

    //カテゴリが見つからない
    if (state.categories[categoryIndex] === undefined) {
      return false;
    }

    //送信内容の作成
    const body = new FormData();
    body.append('name', state.categories[categoryIndex].name);
    body.append('order_number', categoryIndex + 1);
    body.append('thumbnail', state.categories[categoryIndex].thumbnail);
    body.append('is_view', state.categories[categoryIndex].is_view);
    if (otherCategoryId !== null) {
      body.append('other_post_category_id', otherCategoryId);
    }

    //新規追加されたタグのみ拾う
    for (const tag of state.categories[categoryIndex].tags) {
      body.append('tags['+tag.id+'][id]', tag.id);
      body.append('tags['+tag.id+'][name]', tag.name);
      body.append('tags['+tag.id+'][campaign]', state.temporaryCampaignTagIds.some(tagId => Number(tagId) === Number(tag.id)) ? '1' : '0');
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/set-category/'+(state.categories[categoryIndex].id < 1 ? 0 : state.categories[categoryIndex].id), body);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //成功したらローカルの値も変更する
    state.categories = result.categories;
    state.campaigns = result.campaigns;
    state.otherCategoryId = result.otherCategoryId;

    //成功を返す
    return true;
  };

  /**
   * 現在の並び順でカテゴリを並び替える
   * @returns {Promise<boolean>}
   */
  const setCategoryOrders = async () => {
    //エラーを初期化
    state.errors = [];

    //送信内容の作成
    const body = new FormData();

    //現在の並び順で ID を送信内容に挿入
    for (const category of state.categories) {
      body.append('category_ids[]', category.id);
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/set-category-orders', body);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //成功を返す
    return true;
  };

  /**
   * 現在の並び順でタグを並び替える
   * @param categoryIndex
   * @returns {Promise<boolean>}
   */
  const setTagOrders = async (categoryIndex) => {
    //エラーを初期化
    state.errors = [];

    //カテゴリが見つからない
    if (state.categories[categoryIndex] === undefined) {
      return false;
    }

    //送信内容の作成
    const body = new FormData();

    //現在の並び順で ID を送信内容に挿入
    for (const tag of state.categories[categoryIndex].tags) {
      body.append('tag_ids[]', tag.id);
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/set-tag-orders', body);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //成功を返す
    return true;
  };

  /**
   * 指定されたカテゴリを削除する
   * @param categoryIndex
   * @returns {Promise<boolean>}
   */
  const deleteCategory = async (categoryIndex) => {
    //エラーを初期化
    state.errors = [];

    //カテゴリが見つからない
    if (state.categories[categoryIndex] === undefined) {
      return false;
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/delete-category/'+state.categories[categoryIndex].id);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //カテゴリを実際に削除する
    state.categories.splice(categoryIndex, 1);

    //成功を返す
    return true;
  };

  /**
   * 指定されたタグを削除する
   * @param categoryIndex
   * @param tagIndex
   * @returns {Promise<boolean>}
   */
  const deleteTag = async (categoryIndex, tagIndex) => {
    //エラーを初期化
    state.errors = [];

    //カテゴリが見つからない
    if (state.categories[categoryIndex] === undefined) {
      return false;
    }

    //タグが見つからない
    if (state.categories[categoryIndex].tags[tagIndex] === undefined) {
      return false;
    }

    //タグ ID が 0 未満であれば、まだサーバーに登録されていないタグなのでローカル値のみ削除する
    if (Number(state.categories[categoryIndex].tags[tagIndex].id) < 0) {
      //ローカル値を削除
      state.categories[categoryIndex].tags.splice(tagIndex, 1);

      //成功を返す
      return true;
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/delete-tag/'+state.categories[categoryIndex].tags[tagIndex].id);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //タグを実際に削除する
    state.categories[categoryIndex].tags.splice(tagIndex, 1);

    //成功を返す
    return true;
  };

  /**
   * 指定したタグをキャンペーンに登録する
   * @param tagId
   * @returns {Promise<boolean>}
   */
  const setCampaign = async (tagId) => {
    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/set-campaign/'+tagId);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.campaigns = result;

    //成功を返す
    return true;
  };

  /**
   * 指定したタグをキャンペーンから削除する
   * @param tagId
   * @returns {Promise<boolean>}
   */
  const deleteCampaign = async (tagId) => {
    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/delete-campaign/'+tagId);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.campaigns = result;

    //成功を返す
    return true;
  };

  /**
   * 指定したタグ(複数)をキャンペーンから削除する
   * @param tagId
   * @returns {Promise<boolean>}
   */
  const deleteCampaigns = async (tagIds) => {
    //送信パラメータを用意
    const body = new FormData();

    for (const tagId of tagIds) {
      body.append('tag_ids[]', tagId);
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/delete-campaigns', body);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.campaigns = result;

    //成功を返す
    return true;
  };

  /**
   * 指定したタグ(複数)をキャンペーンから削除する
   * @param tagId
   * @returns {Promise<boolean>}
   */
  const releaseCampaigns = async (tagIds) => {
    //送信パラメータを用意
    const body = new FormData();

    for (const tagId of tagIds) {
      body.append('tag_ids[]', tagId);
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/release-campaigns', body);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.campaigns = result;

    //成功を返す
    return true;
  };

  /**
   * 指定したタグ(複数)をキャンペーとして有効にする
   * @param tagId
   * @returns {Promise<boolean>}
   */
  const enableCampaigns = async (tagIds) => {
    //送信パラメータを用意
    const body = new FormData();

    for (const tagId of tagIds) {
      body.append('tag_ids[]', tagId);
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/enable-campaigns', body);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.campaigns = result;

    //成功を返す
    return true;
  };

  /**
   * 指定したカテゴリをキャンペーンに登録する
   * @param tagId
   * @returns {Promise<boolean>}
   */
  const setCategoryCampaign = async (campaignId) => {
    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/set-category-campaign/'+campaignId);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.categoryCampaigns = result;

    //成功を返す
    return true;
  };

  /**
   * 指定したカテゴリをキャンペーンから削除する
   * @param campaignId
   * @returns {Promise<boolean>}
   */
  const deleteCategoryCampaign = async (campaignId) => {
    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/delete-category-campaign/'+campaignId);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.categoryCampaigns = result;

    //成功を返す
    return true;
  };

  /**
   * 指定したカテゴリ(複数)をキャンペーンから削除する
   * @param campaignId
   * @returns {Promise<boolean>}
   */
  const deleteCategoryCampaigns = async (campaignIds) => {
    //送信パラメータを用意
    const body = new FormData();

    for (const campaignId of campaignIds) {
      body.append('category_ids[]', campaignId);
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/delete-category-campaigns', body);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.categoryCampaigns = result;

    //成功を返す
    return true;
  };

  /**
   * 指定したカテゴリ(複数)をキャンペーンから削除する
   * @param campaignId
   * @returns {Promise<boolean>}
   */
  const releaseCategoryCampaigns = async (campaignIds) => {
    //送信パラメータを用意
    const body = new FormData();

    for (const campaignId of campaignIds) {
      body.append('category_ids[]', campaignId);
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/release-category-campaigns', body);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.categoryCampaigns = result;

    //成功を返す
    return true;
  };

  /**
   * 指定したカテゴリ(複数)をキャンペーとして有効にする
   * @param campaignId
   * @returns {Promise<boolean>}
   */
  const enableCategoryCampaigns = async (campaignIds) => {
    //送信パラメータを用意
    const body = new FormData();

    for (const campaignId of campaignIds) {
      body.append('category_ids[]', campaignId);
    }

    //サーバーに依頼
    const result = await _connector.connectPost(siteUrl+'/management/place/enable-category-campaigns', body);

    //失敗したらメッセージを設定
    if (result === null) {
      //メッセージ更新
      if ( ! _connector.isDoubleConnectBlock.value) {
        state.errors = _connector.errors.value;
      }

      //失敗を返す
      return false;
    }

    //キャンペーン情報を更新する
    state.categoryCampaigns = result;

    //成功を返す
    return true;
  };

  //提供
  return {
    state,
    init,
    syncTemporaryCampaignTagIds,
    changeTemporaryCampaign,
    appendEmptyCategory,
    appendTag,
    setCategory,
    setCategoryOrders,
    setTagOrders,
    deleteCategory,
    deleteTag,
    setCampaign,
    deleteCampaign,
    deleteCampaigns,
    releaseCampaigns,
    enableCampaigns,
    setCategoryCampaign,
    deleteCategoryCampaign,
    deleteCategoryCampaigns,
    releaseCategoryCampaigns,
    enableCategoryCampaigns
  };

};

export default useCategoryTag;

export const USE_CATEGORY_TAG_KEY = Symbol('useCategoryTag');
